import { useTranslation } from 'react-i18next'
import { Users } from '@digital-twins/design-system/icons'
import { LibraryCollectionItem } from '@digital-twins/shared-types'

import { CollectionCardLayout, CollectionItemCard, CoverChip, MetaDot, MetaValue } from '@/components/library/CollectionItemCard'
import { ROUTES } from '@/routes/routes'
import { formatSocialDate } from '@/pages/KnowledgeHubPage/social/socialFormat'
import { marketDisplayEntries } from '@/pages/KnowledgeHubPage/libraryHelpers'
import { MarketLabel } from '@/components/ui/MarketLabel'
import { CountryFlag } from '@/components/ui/CountryFlag'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

export interface AudienceCollectionCardProps {
  /** An `audience` item; `item.audience` carries twins, B2B or B2C and Uploaded. */
  item: LibraryCollectionItem
  /** Test ids are `collection-audience-card-{index}`. */
  index: number
  /** The card's place in its section's grid. */
  layout?: CollectionCardLayout
}

/**
 * The resource card frame, with the audience's cover and short description.
 * Respondents and countries live in the footer, alongside type and upload date.
 */
export function AudienceCollectionCard(props: AudienceCollectionCardProps) {
  const { item, index, layout = 'standard' } = props

  const { t, i18n } = useTranslation('app')

  const testId = `collection-audience-card-${index}`
  const summary = item.audience
  const date = formatSocialDate(item.occurredAt, i18n.language)
  const countries = marketDisplayEntries(summary?.countryCodes ?? [])
  const countryNames = countries.map(country => country.label)

  const coverChips = (
    <CoverChip testId={`${testId}-kind`}>
      <Users className="h-3 w-3" aria-hidden="true" />
      {t('library.collections.resources.kindSingular.audience')}
    </CoverChip>
  )

  const metaParts = [
    summary != null ? (
      <MetaValue key="twins" testId={`${testId}-meta-twins`}>
        <span className="inline-flex items-center gap-1.5">
          <Users className="h-3.5 w-3.5" aria-hidden="true" />
          {t('studyLobby.respondents', { count: summary.twinsCount })}
        </span>
      </MetaValue>
    ) : null,
    countries.length === 1 ? (
      <MarketLabel key="countries" label={countries[0].label} code={countries[0].code} data-testid={`${testId}-countries`} />
    ) : countries.length > 1 ? (
      <TooltipProvider key="countries" delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex items-center gap-1.5 underline decoration-dotted underline-offset-2"
              aria-label={t('library.marketsAria', { list: countryNames.join(', ') })} data-testid={`${testId}-countries`}>
              <CountryFlag code={null} />
              {t('audiences.countriesCount', { count: countries.length })}
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-[240px] text-[12px]">{countryNames.join(', ')}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ) : null,
    summary?.audienceType ? (
      <MetaValue key="type" testId={`${testId}-type`}>
        {t(`library.collections.resources.audienceType.${summary.audienceType}`)}
      </MetaValue>
    ) : null,
    // Uploaded folds into the date, so it reads as when, not as a bare label.
    summary?.uploaded ? (
      <MetaValue key="uploaded" testId={`${testId}-uploaded`}>
        {date
          ? t('library.collections.resources.uploadedOn', { date })
          : t('library.collections.resources.uploaded')}
      </MetaValue>
    ) : null,
    date && !summary?.uploaded ? (
      <MetaValue key="date" testId={`${testId}-date`}>
        {date}
      </MetaValue>
    ) : null,
  ].filter((part) => part != null)

  const meta =
    metaParts.length > 0
      ? metaParts.flatMap((part, position) => (position === 0 ? [part] : [<MetaDot key={`dot-${position}`} />, part]))
      : null

  return (
    <CollectionItemCard
      item={item}
      href={ROUTES.audience(item.token)}
      testId={testId}
      coverChips={coverChips}
      layout={layout}
      portraits={[]}
      meta={meta}
      cta={t('library.collections.resources.cta.audience')}
    />
  )
}
